import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import ThemeToggle from '../components/ThemeToggle';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { signInWithOAuth, selectTeam } from '../services/appwrite';
import { dataApi } from '../services/api';
import { OAUTH_PROVIDERS, EMAIL_METHOD } from '../lib/signInMethods';
import BrandMark from '../components/BrandMark';
import EmailSignInForm from '../components/auth/EmailSignInForm';
import SignInMethodButton from '../components/auth/SignInMethodButton';

import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';

/**
 * Sign up
 *
 * Two steps, and the second one is a single field.
 *
 *   1. Choose a provider, or Email (a one-time sign-in link). No password to
 *      invent, no form to fill in before finding out whether the thing is any
 *      good.
 *   2. Name the shop (and yourself, if you came by Email — a provider tells us
 *      your name, an email address doesn't). That's the entire onboarding
 *      questionnaire — everything
 *      else (address, GST, quote numbering, staff) has a sensible default and
 *      can be changed later in Settings, once they have a reason to care.
 *
 * There is no card, no plan picker and no trial clock: everyone lands on the
 * free plan and stays there until they hit the AI allowance and decide for
 * themselves that they want more.
 *
 * The same route serves both steps. Which one renders depends purely on
 * whether there's a session yet, so an interrupted signup (closed tab, OAuth
 * bounce, refresh) resumes exactly where it left off.
 */

const Register = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [signingIn, setSigningIn] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [organisationName, setOrganisationName] = useState('');
  const [fullName, setFullName] = useState('');
  const [creating, setCreating] = useState(false);
  const [emailMode, setEmailMode] = useState(false);

  /**
   * Once there's a session, ask the server what this account still needs. An
   * account that already belongs to a shop skips straight in — following a
   * stale "Sign up" link should never strand an existing customer on a form
   * asking them to create a second organisation.
   */
  const loadStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const result = await dataApi.signupStatus();
      setStatus(result);

      if (result && !result.needsOrganisation && result.organisations?.length === 1) {
        selectTeam(result.organisations[0].id);
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Could not load signup status:', err);
      toast.error('We could not check your account. Please refresh and try again.');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadStatus();
  }, [isAuthenticated, loadStatus]);

  const handleOAuth = async (provider) => {
    try {
      setSigningIn(true);
      const { error } = await signInWithOAuth(provider);
      if (error) {
        console.error(`${provider} OAuth error:`, error);
        toast.error('Could not start sign-in. Please try again.');
        setSigningIn(false);
      }
      // On success the browser navigates away to the provider — leave the
      // button in its loading state rather than flickering back.
    } catch (err) {
      console.error(`${provider} signup error:`, err);
      toast.error('Something went wrong. Please try again.');
      setSigningIn(false);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();

    const name = organisationName.trim();
    if (name.length < 2) {
      toast.error('Please enter your shop name');
      return;
    }
    if (!status?.name && !fullName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    try {
      setCreating(true);
      const result = await dataApi.signupCreateOrganisation(name, status?.name || fullName.trim());

      if (!result?.organisationId) {
        throw new Error('Signup did not return an organisation');
      }

      // Select the new org before navigating, and use a full page load so auth
      // and licence state initialise cleanly against it.
      selectTeam(result.organisationId);
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Failed to create organisation:', err);
      toast.error(err.message || 'We could not finish setting up your shop. Please try again.');
      setCreating(false);
    }
  };

  // Someone who is already signed in AND already has a shop belongs in the app.
  if (isAuthenticated && status && !status.needsOrganisation && status.organisations?.length > 1) {
    return <Navigate to="/select-organisation" replace />;
  }

  const shell = (children) => (
    <div className="relative min-h-screen flex items-center justify-center bg-background p-4">
      <ThemeToggle />
      <div className="w-full max-w-md">{children}</div>
    </div>
  );

  if (authLoading) {
    return shell(
      <Card>
        <CardContent className="p-8 space-y-4">
          <Skeleton className="h-10 w-40 mx-auto" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </CardContent>
      </Card>
    );
  }

  // =====================================================
  // STEP 2 — name the shop
  // =====================================================
  if (isAuthenticated) {
    if (statusLoading && !status) {
      return shell(
        <Card>
          <CardContent className="p-8 space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
          </CardContent>
        </Card>
      );
    }

    if (status && !status.canCreateOrganisation) {
      return shell(
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center">
              <BrandMark size="lg" />
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-muted-foreground">
              This account has reached the maximum number of shops it can create.
              Get in touch and we'll sort it out.
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/select-organisation">Back to my shops</Link>
            </Button>
          </CardContent>
        </Card>
      );
    }

    return shell(
      <Card>
        <CardHeader className="text-center space-y-2">
          <div className="flex justify-center">
            <BrandMark size="lg" />
          </div>
          <h1 className="text-xl font-semibold">One last thing</h1>
          <p className="text-muted-foreground text-sm">
            {status?.name ? `Nice to meet you, ${status.name.split(' ')[0]}. ` : ''}
            What's your shop called?
          </p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleCreate} className="space-y-5">
            {!status?.name && (
              <div className="space-y-2">
                <Label htmlFor="full-name">Your name</Label>
                <Input
                  id="full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Sam Reed"
                  autoFocus
                  autoComplete="name"
                  maxLength={128}
                  disabled={creating}
                  className="h-12 text-base"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="organisation-name">Shop name</Label>
              <Input
                id="organisation-name"
                value={organisationName}
                onChange={(e) => setOrganisationName(e.target.value)}
                placeholder="e.g. Riverside Panel &amp; Paint"
                autoFocus={!!status?.name}
                autoComplete="organization"
                maxLength={128}
                disabled={creating}
                className="h-12 text-base"
              />
              <p className="text-xs text-muted-foreground">
                This is what your team and your customers will see. You can change it later.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base"
              disabled={creating || organisationName.trim().length < 2 || (!status?.name && !fullName.trim())}
            >
              {creating ? 'Setting up your shop…' : 'Start quoting'}
              {!creating && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>
        </CardContent>
      </Card>
    );
  }

  // =====================================================
  // STEP 1 — choose a provider
  // =====================================================
  return shell(
    <Card>
      <CardHeader className="text-center space-y-2">
        <div className="flex justify-center">
          <BrandMark size="lg" />
        </div>
        <p className="text-muted-foreground text-sm">
          Let's get started - choose how you'd like to sign in.
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {emailMode ? (
          <EmailSignInForm
            submitLabel="Email me a link to continue"
            onBack={() => setEmailMode(false)}
          />
        ) : (
          <div className="space-y-3">
            {OAUTH_PROVIDERS.map((provider) => (
              <SignInMethodButton
                key={provider.id}
                method={provider}
                verb="Continue with"
                busy={signingIn}
                busyLabel="Just a moment…"
                onClick={() => handleOAuth(provider.id)}
              />
            ))}
            <SignInMethodButton
              method={EMAIL_METHOD}
              verb="Continue with"
              disabled={signingIn}
              onClick={() => setEmailMode(true)}
            />
          </div>
        )}

        <div className="space-y-3 text-center text-sm">
          <p className="text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
          <p className="text-xs text-muted-foreground">
            By continuing you agree to our{' '}
            <a
              href="https://panelgear.com/terms"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-foreground"
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://panelgear.com/privacy"
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-foreground"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default Register;
